type FetchInput = RequestInfo | URL;

type FetchInit = RequestInit & { timeoutMs?: number };

/**
 * Wrapper sobre fetch que aborta la request tras el timeout indicado.
 * Sin timeout, la request espera indefinidamente.
 */
export async function fetchWithTimeout(
  input: FetchInput,
  init: FetchInit = {}
): Promise<Response> {
  const { timeoutMs, signal, ...rest } = init;
  const controller = new AbortController();
  const timeoutId = typeof timeoutMs === 'number'
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  const forwardAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener('abort', forwardAbort, { once: true });
    }
  }

  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } catch (error) {
    const err = error as Error;
    if (err?.name === 'AbortError' && typeof timeoutMs === 'number') {
      throw new Error(`Request timed out after ${timeoutMs}ms`);
    }
    throw error;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (signal) {
      signal.removeEventListener('abort', forwardAbort);
    }
  }
}
